import { useState, useEffect, useMemo } from "react";
import { Sv, ic } from "../../components/common/Icons";
import ITAModal from "./ITAModal";
import api from "../../services/api";
import { useITAdminData } from "../../context/ITAdminContext";
import "../../assets/styles/ITAdmin/ITAAssetsPg.css";

const API_BASE = process.env.REACT_APP_API_URL || "/api";
function getToken() {
  const role = sessionStorage.getItem("activeRole");
  if (role) return localStorage.getItem("token_" + role);
  for (const r of ["itadmin","superadmin","hr","employee","pm","intern","admin"]) {
    const t = localStorage.getItem("token_" + r); if (t) return t;
  }
  return null;
}
async function apiCall(path, opts = {}) {
  const token = getToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (!(opts.body instanceof FormData)) headers["Content-Type"] = "application/json";
  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  const text = await res.text();
  let data; try { data = JSON.parse(text); } catch (_) { data = {}; }
  if (!res.ok) throw new Error(data?.error || data?.message || "Request failed");
  return data;
}

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }); } catch { return d; }
};
const today = () => new Date().toISOString().split("T")[0];
const monthKey = (d) => {
  if (!d) return null;
  try { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`; } catch { return null; }
};
const monthLabel = (key) => {
  if (!key) return "";
  const [y,m] = key.split("-");
  const dt = new Date(Number(y), Number(m)-1, 1);
  return dt.toLocaleDateString("en-IN", { month:"long", year:"numeric" });
};

function calcDuration(startDate, endDate) {
  if (!startDate) return null;
  try {
    const s = new Date(startDate);
    const e = endDate ? new Date(endDate) : new Date();
    const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
    if (months < 1) return "< 1 mo";
    const yrs = Math.floor(months / 12);
    const mos = months % 12;
    if (yrs === 0) return `${mos} mo`;
    if (mos === 0) return `${yrs} yr`;
    return `${yrs} yr ${mos} mo`;
  } catch { return null; }
}

const conditionColor = (c) => {
  if (c === "New" || c === "Good") return { bg:"#eff6ff", color:"#2563eb" };
  if (c === "Fair") return { bg:"#fef3c7", color:"#d97706" };
  if (c === "Poor" || c === "Damaged") return { bg:"#fef2f2", color:"#ef4444" };
  if (c === "Lost") return { bg:"#1e1b4b", color:"#fff" };
  return { bg:"#f3f4f6", color:"#374151" };
};
const statusColor = (s) => {
  if (s === "Assigned") return { bg:"#eff6ff", color:"#2563eb" };
  if (s === "Available") return { bg:"#eef2ff", color:"#4f46e5" };
  if (s === "Returned") return { bg:"#f3f4f6", color:"#64748b" };
  if (s === "Lost") return { bg:"#1e1b4b", color:"#fff" };
  return { bg:"#fef2f2", color:"#ef4444" };
};

function exportCSV(rows, filename) {
  const headers = ["Asset ID","Tag","Type","Brand","Serial","Value (INR)","Employee","Issued Date","Issued By","Purchased","Condition","Status","Warranty Date","Last Maintenance","Next Maintenance","Last Assigned To","Last Assigned ID","Reassigned On","Held For"];
  const lines = [headers.join(",")];
  rows.forEach(a => {
    const duration = calcDuration(a.prev_assignment_start, a.prev_assignment_end);
    lines.push([
      a.aid, a.asset_tag||"", a.assetType, a.brand, a.serial,
      parseFloat(a.asset_value||0).toFixed(2), a.empName||"",
      a.issued ? new Date(a.issued).toLocaleDateString("en-IN") : "",
      a.issued_by||"",
      a.purchased ? new Date(a.purchased).toLocaleDateString("en-IN") : "",
      a.condition, a.status,
      a.warrantyDate ? new Date(a.warrantyDate).toLocaleDateString("en-IN") : "",
      a.last_maintenance_date ? new Date(a.last_maintenance_date).toLocaleDateString("en-IN") : "",
      a.next_maintenance_date ? new Date(a.next_maintenance_date).toLocaleDateString("en-IN") : "",
      a.last_assigned_emp||"", a.last_assigned_emp_id||"",
      a.last_assignment_date ? new Date(a.last_assignment_date).toLocaleDateString("en-IN") : "",
      duration||"",
    ].map(v => `"${String(v||"").replace(/"/g,"\"")}"`).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type:"text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

function StatBadge({ label, count, color, active, onClick }) {
  const ICONS = { All:"📦", Assigned:"👤", Available:"✅", Returned:"↩️", Damaged:"⚠️", Lost:"🔴" };
  return (
    <button onClick={onClick} style={{
      display:"flex", flexDirection:"column", alignItems:"center",
      padding:"10px 16px", borderRadius:12, border:"none", cursor:"pointer", minWidth:78,
      background: active ? color : "#f8fafc",
      boxShadow: active ? `0 4px 14px ${color}44` : "0 1px 4px rgba(0,0,0,.06)",
      transition:"all 0.18s ease"
    }}>
      <span style={{ fontSize:17, marginBottom:1 }}>{ICONS[label] || "📦"}</span>
      <span style={{ fontSize:22, fontWeight:800, color: active?"#fff":color, lineHeight:1.1 }}>{count}</span>
      <span style={{ fontSize:10, fontWeight:600, color: active?"rgba(255,255,255,.85)":"#64748b", marginTop:2 }}>{label}</span>
    </button>
  );
}

function MaintenanceModal({ asset, onClose, onSave }) {
  const [nextDate, setNextDate] = useState("");
  const [saving, setSaving] = useState(false);
  if (!asset) return null;
  const handleSave = async () => {
    if (!nextDate) { alert("Please select next maintenance date"); return; }
    setSaving(true);
    try {
      await apiCall("/itadmin/maintenance", { method:"PUT", body: JSON.stringify({ assetId: asset.aid, nextMaintenanceDate: nextDate }) });
      window.dispatchEvent(new CustomEvent("maintenance:updated"));
      onSave(); onClose();
    } catch (e) { alert(e.message); setSaving(false); }
  };
  return (
    <ITAModal open={true} onClose={onClose} title={`Maintenance — ${asset.brand || asset.aid}`}>
      <div style={{ padding:"4px 0 12px" }}>
        <div style={{ display:"grid", gap:8, marginBottom:14, background:"#f8fafc", borderRadius:8, padding:"10px 14px" }}>
          <div style={{ fontSize:12, color:"#64748b" }}>Last Maintenance: <strong style={{ color:"#334155" }}>{fmtDate(asset.last_maintenance_date)}</strong></div>
          <div style={{ fontSize:12, color:"#64748b" }}>Current Next: <strong style={{ color:"#ef4444" }}>{fmtDate(asset.next_maintenance_date)}</strong></div>
        </div>
        <p style={{ fontSize:12, color:"#6366f1", marginBottom:10, fontStyle:"italic" }}>ℹ️ Current "Next Maintenance" will move to "Last Maintenance" automatically.</p>
        <div className="form-group">
          <label className="form-label">New Next Maintenance Date *</label>
          <input type="date" className="form-input" min={today()} value={nextDate} onChange={e => setNextDate(e.target.value)} />
        </div>
        <div className="form-actions" style={{ justifyContent:"flex-end", gap:10, marginTop:12 }}>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn-submit" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Maintenance"}</button>
        </div>
      </div>
    </ITAModal>
  );
}

function LostAssetFormModal({ asset, onClose, onSave }) {
  const [lostDate, setLostDate] = useState(today());
  const [empInfo, setEmpInfo] = useState(null);
  const [allPMs, setAllPMs] = useState([]);
  const [selectedPmId, setSelectedPmId] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const data = await apiCall(`/itadmin/asset-lost-info/${asset.aid}`); setEmpInfo(data.employee||null); if (data.pm?.id) setSelectedPmId(String(data.pm.id)); } catch(e) { console.warn(e); }
      try { const pms = await apiCall("/itadmin/pms-list"); setAllPMs(Array.isArray(pms)?pms:[]); } catch(e) { console.warn(e); }
      setLoading(false);
    })();
  }, [asset.aid]);
  const selectedPM = allPMs.find(p => String(p.id) === String(selectedPmId));
  const handleSend = async () => {
    if (!lostDate) { alert("Please select the lost date"); return; }
    if (!selectedPmId) { alert("Please select a Project Manager"); return; }
    if (!empInfo) { alert("This asset has no assigned employee."); return; }
    setSending(true);
    try {
      await apiCall("/itadmin/lost-asset-report", { method:"POST", body: JSON.stringify({ assetId:asset.aid, lostDate, employeeId:empInfo?.id, pmId:selectedPmId }) });
      onSave(); onClose();
    } catch(e) { alert(e.message); setSending(false); }
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.55)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center", padding:16, overflowY:"auto" }}>
      <div style={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:580, boxShadow:"0 24px 80px rgba(0,0,0,.3)" }}>
        <div style={{ background:"linear-gradient(135deg,#dc2626,#991b1b)", color:"#fff", padding:"14px 20px", borderRadius:"16px 16px 0 0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div><div style={{ fontWeight:700, fontSize:15 }}>🔴 Lost Asset Report</div><div style={{ fontSize:12, opacity:.8, marginTop:2 }}>Select PM → Send Report → PM adds fine → HR notifies employee</div></div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,.2)", border:"none", color:"#fff", borderRadius:8, width:30, height:30, cursor:"pointer", fontSize:18 }}>×</button>
        </div>
        <div style={{ padding:20 }}>
          {loading ? <div style={{ textAlign:"center", padding:40, color:"#64748b" }}>Loading details...</div> : (
            <>
              <div style={{ border:"1px solid #e2e8f0", borderRadius:12, overflow:"hidden", marginBottom:16 }}>
                <div style={{ background:"#1e1b4b", color:"#fff", padding:"10px 16px", fontWeight:700, fontSize:13 }}>📋 LOST ASSET REPORT</div>
                <Section title="👤 Employee"><InfoRow2 label="Name" val={empInfo?.name||"—"}/><InfoRow2 label="Employee ID" val={empInfo?.emp_id||"—"}/><InfoRow2 label="Designation" val={empInfo?.designation||"—"}/><InfoRow2 label="Department" val={empInfo?.department||"—"}/></Section>
                <Section title="💻 Asset"><InfoRow2 label="Asset ID" val={asset.aid}/><InfoRow2 label="Brand/Model" val={asset.brand||"—"}/><InfoRow2 label="Asset Tag" val={asset.asset_tag||"—"}/><InfoRow2 label="Value" val={`₹${parseFloat(asset.asset_value||0).toLocaleString("en-IN",{minimumFractionDigits:2})}`} accent="#dc2626"/><InfoRow2 label="Issued" val={fmtDate(asset.issued)}/><InfoRow2 label="Issued By" val={asset.issued_by||"—"}/></Section>
                <div style={{ padding:"12px 16px", borderTop:"1px solid #f1f5f9" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>📅 Lost Date *</div>
                  <input type="date" max={today()} value={lostDate} onChange={e => setLostDate(e.target.value)} style={{ padding:"8px 10px", border:"1.5px solid #6366f1", borderRadius:8, fontSize:13, width:200, outline:"none" }} />
                </div>
                <div style={{ padding:"14px 16px", borderTop:"1px solid #f1f5f9", background:"#fafafa" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>👨‍💼 Select Project Manager *</div>
                  {allPMs.length === 0 ? <div style={{ fontSize:12, color:"#ef4444" }}>No PMs found.</div> : (
                    <select value={selectedPmId} onChange={e => setSelectedPmId(e.target.value)} style={{ width:"100%", padding:"10px 12px", border:"2px solid #dc2626", borderRadius:8, fontSize:13, background:"#fff", outline:"none", cursor:"pointer", fontWeight:600 }}>
                      <option value="">— Select Project Manager —</option>
                      {allPMs.map(pm => <option key={pm.id} value={pm.id}>{pm.name}</option>)}
                    </select>
                  )}
                  {selectedPM && <div style={{ marginTop:8, padding:"6px 10px", background:"#dcfce7", borderRadius:6, fontSize:12, color:"#15803d", fontWeight:600 }}>✅ Report will be sent to <strong>{selectedPM.name}</strong>.</div>}
                </div>
              </div>
              {!empInfo && <div style={{ background:"#fef3c7", border:"1px solid #fde68a", borderRadius:8, padding:"10px 12px", fontSize:12, color:"#92400e", marginBottom:12 }}>⚠️ No assigned employee. Please assign before filing lost report.</div>}
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:4 }}>
                <button onClick={onClose} style={{ padding:"9px 18px", border:"1px solid #e2e8f0", borderRadius:8, background:"#fff", cursor:"pointer", fontSize:13 }}>Cancel</button>
                <button onClick={handleSend} disabled={sending||!empInfo||!selectedPmId} style={{ padding:"9px 22px", background:(!empInfo||!selectedPmId||sending)?"#fca5a5":"#dc2626", color:"#fff", border:"none", borderRadius:8, fontWeight:700, cursor:(!empInfo||!selectedPmId)?"not-allowed":"pointer", fontSize:13 }}>
                  {sending ? "Sending..." : `📤 Send to ${selectedPM?selectedPM.name:"PM"}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ borderTop:"1px solid #f1f5f9" }}>
      <div style={{ background:"#f8fafc", padding:"6px 16px", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:.5 }}>{title}</div>
      <div style={{ padding:"8px 16px 4px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 16px" }}>{children}</div>
    </div>
  );
}
function InfoRow2({ label, val, accent }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", paddingBottom:6 }}>
      <span style={{ fontSize:10, color:"#94a3b8", textTransform:"uppercase", letterSpacing:.3 }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:600, color: accent || "#1e293b" }}>{val || "—"}</span>
    </div>
  );
}

function downloadDemoCSV() {
  const rows = [
    "# AMSHRMS Bulk Asset Upload — Demo File",
    "# Required: asset_type, brand",
    "# Optional: serial, asset_tag, value, emp_id, condition, warranty, purchased_date, warranty_date, issued_date, issued_by",
    "# issued_date: YYYY-MM-DD format. Leave blank to auto-set on assignment. issued_by: IT Admin name",
    "asset_type,brand,serial,asset_tag,value,emp_id,condition,warranty,purchased_date,warranty_date,last_maintenance_date,next_maintenance_date,issued_date,issued_by",
    "Laptop,Dell,SN-DELL-001,TAG-001,45000,,New,24,2024-01-15,2026-01-15,2024-06-01,2025-06-01,,",
    "Monitor,Samsung,SN-SAM-002,TAG-002,15000,,Good,12,2024-02-10,2025-02-10,,,2024-02-10,Karthik",
    "Desktop,HP,SN-HP-003,TAG-003,55000,,Fair,24,2023-11-01,2025-11-01,,,,"
  ];
  const blob = new Blob([rows.join("\n")], { type:"text/csv;charset=utf-8;" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
  a.download = "assets_bulk_upload_demo.csv"; a.click();
  URL.revokeObjectURL(a.href);
}

function LastAssignedBadge({ asset }) {
  if (!asset.last_assigned_emp) return <span style={{ color:"#94a3b8", fontSize:11 }}>—</span>;
  const duration = calcDuration(asset.prev_assignment_start, asset.prev_assignment_end);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
      <span style={{ fontSize:12, fontWeight:600, color:"#374151" }}>{asset.last_assigned_emp}</span>
      {asset.last_assigned_emp_id && <span style={{ fontSize:10, color:"#64748b" }}>ID: {asset.last_assigned_emp_id}</span>}
      {asset.last_assignment_date && <span style={{ fontSize:10, color:"#6366f1" }}>Reassigned: {fmtDate(asset.last_assignment_date)}</span>}
      {duration && <span style={{ display:"inline-block", marginTop:2, padding:"1px 6px", borderRadius:10, background:"#f0fdf4", color:"#15803d", fontSize:10, fontWeight:700 }}>Held {duration}</span>}
    </div>
  );
}

function ITAAssetsPg() {
  const { refreshITAdminData } = useITAdminData();
  const [aList, setAList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [itAdmins, setItAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showMaintenance, setShowMaintenance] = useState(null);
  const [showLostForm, setShowLostForm] = useState(null);
  const [suc, setSuc] = useState("");
  const [filter, setFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [showMonthly, setShowMonthly] = useState(false);
  const [activeTab, setActiveTab] = useState("assets");
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkDragOver, setBulkDragOver] = useState(false);
  const [uploadLogs, setUploadLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [onboardingQueue, setOnboardingQueue] = useState([]);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  const emptyAdd = { assetType:"Laptop", assetTag:"", brand:"", serial:"", assetValue:"", condition:"New", warranty:"", purchasedDate:"", warrantyDate:"", empId:"", lastMaintenanceDate:"", nextMaintenanceDate:"", issuedDate:"", issuedBy:"" };
  const [form, setForm] = useState(emptyAdd);
  const [editForm, setEditForm] = useState({});

  useEffect(() => { fetchAssets(); fetchEmployees(); fetchITAdmins(); }, []);
  useEffect(() => { if (activeTab === 'onboarding') fetchOnboardingQueue(); }, [activeTab]);

  const mapAsset = (a) => ({
    aid: a.asset_id, asset_tag: a.asset_tag||"", asset_value: a.asset_value||0,
    empId: a.assigned_emp_id||"", empName: a.emp_name||"",
    assetType: a.asset_type, brand: a.brand, serial: a.serial,
    issued: a.issued_date||"", created: a.created_date||"", purchased: a.purchased_date||"",
    condition: a.condition, status: a.status, warranty: a.warranty||"",
    warrantyDate: a.warranty_date||"", last_maintenance_date: a.last_maintenance_date||"",
    next_maintenance_date: a.next_maintenance_date||"", lostAmount: a.lost_amount||0,
    lostDate: a.lost_date||"", warrantyDone: a.warranty_done||0, designation: a.designation||"",
    issued_by: a.issued_by||"",
    last_assigned_emp: a.last_assigned_emp||"", last_assigned_emp_id: a.last_assigned_emp_id||"",
    last_assignment_date: a.last_assignment_date||"",
    prev_assignment_start: a.prev_assignment_start||"", prev_assignment_end: a.prev_assignment_end||"",
  });

  const fetchAssets = async () => {
    try { const data = await api.getITAssets(); setAList((data||[]).map(mapAsset)); }
    catch(e) { console.warn(e.message); } finally { setLoading(false); }
    // Refresh context so dashboard KPIs update
    refreshITAdminData().catch(()=>{});
  };
  const fetchEmployees = async () => {
    try { const data = await api.getITPeople(); setEmployees((data||[]).map(p=>({id:p.id||p.emp_id,name:p.name,status:p.status}))); }
    catch(e) { console.warn(e); }
  };
  const fetchITAdmins = async () => {
    try { const data = await api.getITAdminsList(); setItAdmins(Array.isArray(data)?data:[]); }
    catch(e) { console.warn(e); }
  };

  const fetchOnboardingQueue = async () => {
    setOnboardingLoading(true);
    try { const data = await api.getITOnboardingQueue(); setOnboardingQueue(Array.isArray(data)?data:[]); }
    catch(e) { console.warn(e); setOnboardingQueue([]); }
    setOnboardingLoading(false);
  };

  const showSuccess = (msg) => { setSuc(msg); setTimeout(()=>setSuc(""),4000); };

  const addAsset = async () => {
    if (!form.brand||!form.serial) { alert("Brand and Serial are required"); return; }
    try {
      await apiCall("/itadmin/assets", { method:"POST", body: JSON.stringify({
        assetType:form.assetType, brand:form.brand, serial:form.serial, empId:form.empId,
        assetCondition:form.condition, warranty:form.warranty, assetTag:form.assetTag,
        assetValue:form.assetValue, purchasedDate:form.purchasedDate, warrantyDate:form.warrantyDate,
        lastMaintenanceDate:form.lastMaintenanceDate, nextMaintenanceDate:form.nextMaintenanceDate,
        issuedDate:form.issuedDate||undefined, issuedBy:form.issuedBy||undefined,
      })});
      showSuccess("Asset added successfully!"); setShowAdd(false); setForm(emptyAdd); await fetchAssets();
    } catch(e) { alert("Error: "+e.message); }
  };

  const openEdit = (a) => {
    setEditForm({ condition:a.condition, status:a.status, empId:a.empId, assetTag:a.asset_tag, assetValue:a.asset_value,
      purchasedDate:a.purchased?a.purchased.split("T")[0]:"", warrantyDate:a.warrantyDate?a.warrantyDate.split("T")[0]:"",
      issuedDate:a.issued?a.issued.split("T")[0]:"", issuedBy:a.issued_by||"" });
    setShowEdit(a);
  };

  const saveEdit = async () => {
    if (!showEdit) return;
    if (editForm.condition==="Lost") { setShowEdit(null); setTimeout(()=>setShowLostForm(showEdit),200); return; }
    let autoStatus = editForm.status;
    if (editForm.empId) autoStatus = "Assigned";
    else if (!editForm.empId&&showEdit.empId) autoStatus = "Available";
    try {
      await apiCall("/itadmin/assets", { method:"PUT", body: JSON.stringify({
        assetId:showEdit.aid, assetCondition:editForm.condition, status:autoStatus,
        empId:editForm.empId, assetTag:editForm.assetTag, assetValue:editForm.assetValue,
        purchasedDate:editForm.purchasedDate, warrantyDate:editForm.warrantyDate,
      })});
      showSuccess("Asset updated!"); setShowEdit(null); await fetchAssets();
    } catch(e) { alert("Error: "+e.message); }
  };

  const deleteAsset = async (a) => {
    if (!window.confirm(`Delete asset ${a.aid} (${a.brand})?`)) return;
    try { await api.deleteITAsset(a.aid); showSuccess("Asset deleted!"); await fetchAssets(); }
    catch(e) { alert("Error: "+e.message); }
  };

  const FILTERS = ["all","Assigned","Available","Returned","Damaged","Lost"];
  const STATUS_COLORS = { all:"#334155", Assigned:"#2563eb", Available:"#4f46e5", Returned:"#64748b", Damaged:"#ef4444", Lost:"#1e1b4b" };

  const counts = useMemo(() => {
    const c = { all:aList.length, Assigned:0, Available:0, Returned:0, Damaged:0, Lost:0 };
    aList.forEach(a => { if (a.status==="Lost"||a.condition==="Lost") c.Lost++; else if (c[a.status]!==undefined) c[a.status]++; });
    return c;
  }, [aList]);

  const availMonths = useMemo(() => {
    const set = new Set();
    aList.forEach(a => { const k = monthKey(a.created||a.purchased||a.issued); if (k) set.add(k); });
    return ["all",...Array.from(set).sort((a,b)=>b.localeCompare(a))];
  }, [aList]);

  const filtered = useMemo(() => {
    let list = aList;
    if (filter==="Lost") list = list.filter(a=>a.status==="Lost"||a.condition==="Lost");
    else if (filter!=="all") list = list.filter(a=>a.status===filter);
    if (monthFilter!=="all") list = list.filter(a=>{ const k=monthKey(a.created||a.purchased||a.issued); return k===monthFilter; });
    return list;
  }, [aList,filter,monthFilter]);

  const csvName = () => { const p=["assets"]; if(filter!=="all")p.push(filter.toLowerCase()); if(monthFilter!=="all")p.push(monthLabel(monthFilter).replace(" ","_")); return p.join("_")+".csv"; };

  return (
    <div>
      <div className="itaassetspg-inline-1">
        <div className="itaassetspg-inline-2">Asset Management</div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ position:"relative" }}>
            <button onClick={()=>setShowMonthly(v=>!v)} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, fontWeight:600, fontSize:12, color:"#374151", cursor:"pointer" }}>📅 Monthly CSV ▾</button>
            {showMonthly && (
              <div style={{ position:"absolute", right:0, top:"calc(100% + 6px)", background:"#fff", border:"1px solid #e2e8f0", borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,.12)", zIndex:100, minWidth:220, overflow:"hidden" }}>
                <div style={{ padding:"8px 12px", fontSize:11, fontWeight:700, color:"#64748b", borderBottom:"1px solid #f1f5f9", background:"#f8fafc" }}>SELECT MONTH TO DOWNLOAD</div>
                {availMonths.filter(m=>m!=="all").length===0 ? <div style={{ padding:"12px", fontSize:12, color:"#94a3b8" }}>No data yet</div>
                : availMonths.filter(m=>m!=="all").map(m => {
                  const mRows = aList.filter(a=>monthKey(a.created||a.purchased||a.issued)===m);
                  return <button key={m} onClick={()=>{ exportCSV(mRows,`assets_${m}.csv`); setShowMonthly(false); }} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", width:"100%", padding:"10px 14px", border:"none", background:"#fff", cursor:"pointer", fontSize:13, textAlign:"left", borderBottom:"1px solid #f8fafc" }} onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}><span style={{ fontWeight:600, color:"#1e293b" }}>{monthLabel(m)}</span><span style={{ fontSize:11, color:"#94a3b8" }}>{mRows.length} assets</span></button>;
                })}
              </div>
            )}
          </div>
          <button onClick={()=>exportCSV(filtered,csvName())} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", background:"#0f172a", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer" }}>⬇ Download CSV {filtered.length!==aList.length?`(${filtered.length})`:""}</button>
          <button className="btn-outline itaassetspg-inline-3" onClick={()=>setShowAdd(true)}><Sv d={ic.plus} s={14} /> Add Asset</button>
          <button onClick={()=>{ setActiveTab(t=>t==="bulk"?"assets":"bulk"); setBulkResult(null); setBulkFile(null); }} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", background:activeTab==="bulk"?"#4338ca":"#fff", color:activeTab==="bulk"?"#fff":"#4338ca", border:"1.5px solid #4338ca", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer" }}>⬆ Bulk Upload</button>
          {/* <button onClick={()=>setActiveTab(t=>t==="onboarding"?"assets":"onboarding")}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px",
              background:activeTab==="onboarding"?"#059669":"#fff",
              color:activeTab==="onboarding"?"#fff":"#059669",
              border:"1.5px solid #059669", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer", position:"relative" }}>
            🆕 Onboarding
            {onboardingQueue.filter(u=>u.asset_count===0).length>0&&(
              <span style={{ position:"absolute", top:-6, right:-6, background:"#ef4444", color:"#fff", borderRadius:99, fontSize:10, fontWeight:800, minWidth:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px" }}>
                {onboardingQueue.filter(u=>u.asset_count===0).length}
              </span>
            )}
          </button> */}
          <button onClick={()=>{ setActiveTab("logs"); setLogsLoading(true); api.getITLogs().then(d=>{setUploadLogs((Array.isArray(d)?d:[]).filter(l=>l.action&&l.action.toLowerCase().includes("asset")));}).catch(()=>setUploadLogs([])).finally(()=>setLogsLoading(false)); }} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", background:activeTab==="logs"?"#0f172a":"#fff", color:activeTab==="logs"?"#fff":"#0f172a", border:"1.5px solid #0f172a", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer" }}>📋 Upload Logs</button>
        </div>
      </div>

      {suc && <div className="itaassetspg-inline-4"><Sv d={ic.chk} s={16} c="#2563eb" /> {suc}</div>}

      {activeTab==="bulk" && (
        <div style={{ background:"#fff", border:"1px solid #e8eaed", borderRadius:8, overflow:"hidden" }}>
          <div style={{ padding:"18px 24px", borderBottom:"1px solid #e8eaed", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div><div style={{ fontWeight:700, fontSize:15, color:"#1a1a2e" }}>Bulk Asset Upload</div><div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>Upload a CSV to add multiple assets at once</div></div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setShowDemo(v=>!v)} style={{ padding:"7px 16px", background:"#fff", border:"1px solid #d1d5db", borderRadius:6, fontWeight:600, fontSize:12, color:"#374151", cursor:"pointer" }}>{showDemo?"Hide Demo File":"Show Demo File"}</button>
              <button onClick={downloadDemoCSV} style={{ padding:"7px 16px", background:"#fff", border:"1px solid #d1d5db", borderRadius:6, fontWeight:600, fontSize:12, color:"#374151", cursor:"pointer" }}>Download Sample CSV</button>
            </div>
          </div>
          {showDemo && (
            <div style={{ borderBottom:"1px solid #e8eaed", overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, minWidth:1000 }}>
                <thead><tr style={{ background:"#f9fafb" }}>
                  {["ASSET_TYPE","BRAND","SERIAL","ASSET_TAG","VALUE","EMP_ID","CONDITION","WARRANTY","PURCHASED_DATE","WARRANTY_DATE","LAST_MAINT","NEXT_MAINT","ISSUED_DATE ⬤","ISSUED_BY ⬤"].map(h=>(
                    <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:h.includes("⬤")?"#7c3aed":"#6b7280", letterSpacing:".4px", borderBottom:"1px solid #e8eaed", whiteSpace:"nowrap" }}>{h.replace(" ⬤","")}{h.includes("⬤")&&<span style={{ fontSize:9, background:"#ede9fe", color:"#7c3aed", padding:"1px 4px", borderRadius:4, marginLeft:4 }}>NEW</span>}</th>
                  ))}</tr></thead>
                <tbody>
                  {[["Laptop","Dell","SN-DELL-001","TAG-001","45000","","New","24","2024-01-15","2026-01-15","","","",""],
                    ["Monitor","Samsung","SN-SAM-002","TAG-002","15000","","Good","12","2024-02-10","2025-02-10","","","2024-02-10","Karthik"],
                    ["Desktop","HP","SN-HP-003","TAG-003","55000","","Fair","24","2023-11-01","2025-11-01","","","",""]
                  ].map((row,ri)=>(
                    <tr key={ri} style={{ background:ri%2===0?"#fff":"#fafafa" }}>
                      {row.map((v,i)=><td key={i} style={{ padding:"10px 14px", fontSize:12, color:i>=12?"#7c3aed":"#374151", fontWeight:i>=12&&v?600:400, borderBottom:"1px solid #f3f4f6" }}>{v||<span style={{color:"#d1d5db"}}>—</span>}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* <div style={{ padding:"8px 14px", background:"#f9fafb", fontSize:11, color:"#9ca3af" }}>
                * <strong>Required:</strong> asset_type, brand &nbsp;|&nbsp; <strong style={{color:"#7c3aed"}}>NEW:</strong> issued_date (YYYY-MM-DD) + issued_by (select from IT Admin list)
              </div> */}
            </div>
          )}
          <div style={{ padding:"24px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, padding:"10px 14px", border:"1px solid #e8eaed", borderRadius:6, background:"#fafafa" }}>
              <label style={{ display:"flex", alignItems:"center", cursor:"pointer" }}>
                <span style={{ padding:"6px 12px", border:"1px solid #d1d5db", borderRadius:4, background:"#fff", fontSize:12, fontWeight:500, color:"#374151", cursor:"pointer", boxShadow:"0 1px 2px rgba(0,0,0,.05)" }}>Choose File</span>
                <input type="file" accept=".csv" style={{ display:"none" }} onChange={e=>{ const f=e.target.files[0]; if(f){setBulkFile(f);setBulkResult(null);e.target.value="";} }} />
              </label>
              <span style={{ fontSize:12, color:bulkFile?"#374151":"#9ca3af" }}>{bulkFile?bulkFile.name:"No file chosen"}</span>
              {bulkFile && <><span style={{ fontSize:11, color:"#9ca3af" }}>({(bulkFile.size/1024).toFixed(1)} KB)</span><button onClick={()=>{setBulkFile(null);setBulkResult(null);}} style={{ marginLeft:"auto", padding:"3px 10px", border:"1px solid #fecaca", borderRadius:4, background:"#fef2f2", color:"#ef4444", fontWeight:600, fontSize:11, cursor:"pointer" }}>✕ Remove</button></>}
            </div>
            <div onDragOver={e=>{e.preventDefault();setBulkDragOver(true);}} onDragLeave={()=>setBulkDragOver(false)} onDrop={e=>{e.preventDefault();setBulkDragOver(false);const f=e.dataTransfer.files[0];if(f&&f.name.endsWith(".csv")){setBulkFile(f);setBulkResult(null);}}} style={{ border:`2px dashed ${bulkDragOver?"#2563eb":"#e5e7eb"}`, borderRadius:6, padding:"18px", textAlign:"center", marginBottom:20, background:bulkDragOver?"#eff6ff":"#fafafa", transition:"all .15s", fontSize:12, color:"#9ca3af" }}>
              {bulkDragOver?"Drop your CSV file here":"or drag and drop your CSV file here"}
            </div>
            <button disabled={!bulkFile||bulkUploading} onClick={async()=>{
              if(!bulkFile)return; setBulkUploading(true); setBulkResult(null);
              try {
                const result = await api.bulkUploadAssets(bulkFile); setBulkResult(result);
                const count = result.insertedCount??(Array.isArray(result.inserted)?result.inserted.length:0);
                if(count>0){ const raw=await api.getITAssets().catch(()=>[]); setAList((raw||[]).map(mapAsset)); showSuccess(`${count} assets uploaded successfully`); }
              } catch(e){ setBulkResult({error:e.message}); }
              setBulkUploading(false);
            }} style={{ padding:"10px 28px", background:!bulkFile?"#e5e7eb":"#1d4ed8", color:!bulkFile?"#9ca3af":"#fff", border:"none", borderRadius:6, fontWeight:700, fontSize:13, cursor:bulkFile?"pointer":"not-allowed" }}>
              {bulkUploading?"Uploading...":"Upload Assets CSV"}
            </button>
          </div>
          {bulkResult&&!bulkResult.error&&(
            <div style={{ borderTop:"1px solid #e8eaed" }}>
              <div style={{ display:"flex", gap:0, borderBottom:"1px solid #e8eaed" }}>
                {[["Inserted",bulkResult.insertedCount??(Array.isArray(bulkResult.inserted)?bulkResult.inserted.length:bulkResult.inserted),"#16a34a","#f0fdf4"],["Skipped",bulkResult.skipped,"#d97706","#fffbeb"],["Warnings",bulkResult.errors?.length||0,"#dc2626","#fef2f2"]].map(([label,val,color,bg])=>(
                  <div key={label} style={{ flex:1, padding:"16px 24px", background:bg, borderRight:"1px solid #e8eaed", textAlign:"center" }}><div style={{ fontSize:26, fontWeight:800, color }}>{val}</div><div style={{ fontSize:11, color:"#6b7280", fontWeight:600, marginTop:2 }}>{label}</div></div>
                ))}
                <div style={{ display:"flex", alignItems:"center", padding:"0 24px", marginLeft:"auto" }}><button onClick={()=>{setActiveTab("assets");setBulkResult(null);setBulkFile(null);}} style={{ padding:"8px 18px", background:"#1d4ed8", color:"#fff", border:"none", borderRadius:6, fontWeight:700, fontSize:12, cursor:"pointer" }}>View Assets →</button></div>
              </div>
              {Array.isArray(bulkResult.inserted)&&bulkResult.inserted.length>0&&(
                <div>
                  <div style={{ padding:"12px 24px 8px", fontSize:12, fontWeight:700, color:"#374151", borderBottom:"1px solid #f3f4f6" }}>Successfully Added</div>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                      <thead><tr style={{ background:"#f9fafb" }}>{["ASSET ID","TYPE","BRAND","SERIAL","STATUS","ASSIGNED TO","ISSUED DATE","ISSUED BY"].map(h=><th key={h} style={{ padding:"9px 16px", textAlign:"left", fontWeight:700, fontSize:11, color:"#6b7280", letterSpacing:".4px", borderBottom:"1px solid #e8eaed" }}>{h}</th>)}</tr></thead>
                      <tbody>{bulkResult.inserted.map((a,i)=>(
                        <tr key={i} style={{ borderBottom:"1px solid #f3f4f6", background:i%2===0?"#fff":"#fafafa" }}>
                          <td style={{ padding:"9px 16px", fontFamily:"monospace", color:"#1d4ed8", fontWeight:700 }}>{a.assetId}</td>
                          <td style={{ padding:"9px 16px" }}>{a.assetType}</td>
                          <td style={{ padding:"9px 16px", fontWeight:600 }}>{a.brand}</td>
                          <td style={{ padding:"9px 16px", color:"#6b7280", fontFamily:"monospace" }}>{a.serial||"—"}</td>
                          <td style={{ padding:"9px 16px" }}><span style={{ padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:600, background:a.status==="Assigned"?"#dbeafe":"#e0e7ff", color:a.status==="Assigned"?"#1d4ed8":"#4338ca" }}>{a.status}</span></td>
                          <td style={{ padding:"9px 16px", color:"#6b7280" }}>{a.empName||"—"}</td>
                          <td style={{ padding:"9px 16px", color:"#374151" }}>{a.issuedDate?fmtDate(a.issuedDate):"—"}</td>
                          <td style={{ padding:"9px 16px", color:"#7c3aed", fontWeight:a.issuedBy?600:400 }}>{a.issuedBy||"—"}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              )}
              {bulkResult.errors?.length>0&&<div style={{ padding:"12px 24px", background:"#fffbeb", borderTop:"1px solid #fef9c3" }}><div style={{ fontWeight:700, fontSize:12, color:"#92400e", marginBottom:6 }}>Warnings / Skipped Rows</div>{bulkResult.errors.map((e,i)=><div key={i} style={{ fontSize:11, color:"#b45309", padding:"2px 0" }}>• {e}</div>)}</div>}
            </div>
          )}
          {bulkResult?.error&&<div style={{ margin:"0 24px 24px", padding:"12px 16px", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:6, color:"#dc2626", fontSize:13, fontWeight:600 }}>Upload failed: {bulkResult.error}</div>}
        </div>
      )}

      {activeTab==="logs"&&(
        <div>
          <div style={{ background:"linear-gradient(135deg,#0f172a,#1e1b4b)", borderRadius:12, padding:"18px 24px", marginBottom:16, color:"#fff" }}><div style={{ fontWeight:800, fontSize:17, marginBottom:4 }}>📋 Asset Upload Logs</div><div style={{ fontSize:13, opacity:.8 }}>History of all asset additions, bulk uploads, edits, and deletions.</div></div>
          {logsLoading?<div style={{ textAlign:"center", padding:40, color:"#94a3b8", fontSize:14 }}>Loading logs...</div>
          :uploadLogs.length===0?<div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:12, padding:"40px 24px", textAlign:"center" }}><div style={{ fontSize:32, marginBottom:10 }}>📭</div><div style={{ fontWeight:700, color:"#1e293b", marginBottom:6 }}>No asset logs yet</div><div style={{ fontSize:13, color:"#94a3b8" }}>Upload or modify assets to see logs here.</div></div>
          :<div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead><tr style={{ background:"#f8fafc" }}>{["Time","Admin","Action","Target","Details"].map(h=><th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"#475569", borderBottom:"1px solid #e2e8f0", fontSize:12 }}>{h}</th>)}</tr></thead>
              <tbody>{uploadLogs.map((log,i)=>{
                const isBulk=log.action?.toLowerCase().includes("bulk"); const isDel=log.action?.toLowerCase().includes("delet")||log.action?.toLowerCase().includes("remov"); const isEdit=log.action?.toLowerCase().includes("edit")||log.action?.toLowerCase().includes("updat");
                const badgeBg=isBulk?"#eff6ff":isDel?"#fef2f2":isEdit?"#fef9c3":"#f0fdf4"; const badgeClr=isBulk?"#2563eb":isDel?"#dc2626":isEdit?"#d97706":"#16a34a";
                return <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background:i%2===0?"#fff":"#fafbff" }}>
                  <td style={{ padding:"9px 14px", fontSize:11, color:"#64748b", whiteSpace:"nowrap" }}>{log.timestamp_str||(log.created_at?new Date(log.created_at).toLocaleString("en-IN"):"—")}</td>
                  <td style={{ padding:"9px 14px", fontWeight:600, color:"#1e293b" }}>{log.admin_name||"—"}</td>
                  <td style={{ padding:"9px 14px" }}><span style={{ background:badgeBg, color:badgeClr, padding:"2px 10px", borderRadius:20, fontWeight:700, fontSize:11 }}>{log.action||"—"}</span></td>
                  <td style={{ padding:"9px 14px", fontFamily:"monospace", fontSize:12, color:"#4338ca" }}>{log.target||"—"}</td>
                  <td style={{ padding:"9px 14px", fontSize:11, color:"#64748b", maxWidth:320, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={log.detail||""}>{log.detail||"—"}</td>
                </tr>;
              })}</tbody>
            </table>
          </div>}
        </div>
      )}

      {/* ══ ONBOARDING QUEUE TAB ══ */}
      {activeTab==="onboarding"&&(
        <div>
          <div style={{ background:"linear-gradient(135deg,#059669,#047857)", borderRadius:12, padding:"18px 24px", marginBottom:16, color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontWeight:800, fontSize:17, marginBottom:4 }}>🆕 New Employee / Intern Onboarding Queue</div>
              <div style={{ fontSize:13, opacity:.85 }}>Employees who started onboarding — assign assets and credentials when ready</div>
            </div>
            <button onClick={fetchOnboardingQueue} style={{ padding:"7px 14px", background:"rgba(255,255,255,.2)", border:"1px solid rgba(255,255,255,.4)", borderRadius:8, color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>🔄 Refresh</button>
          </div>

          {onboardingLoading ? (
            <div style={{ textAlign:"center", padding:40, color:"#94a3b8" }}>Loading onboarding queue...</div>
          ) : onboardingQueue.length === 0 ? (
            <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:12, padding:"40px 24px", textAlign:"center" }}>
              <div style={{ fontSize:32, marginBottom:10 }}>✅</div>
              <div style={{ fontWeight:700, color:"#1e293b", marginBottom:6 }}>No pending onboarding</div>
              <div style={{ fontSize:13, color:"#94a3b8" }}>New employees will appear here once HR creates their account</div>
            </div>
          ) : (
            <div style={{ display:"grid", gap:10 }}>
              {onboardingQueue.map((u, i) => {
                const isTemp = u.status === "TEMP";
                const isSubmitted = u.status === "ONBOARDING_SUBMITTED";
                const isActive = ["ACTIVE","VERIFIED"].includes(u.status);
                const hasAssets = u.asset_count > 0;
                const hasCreds = u.cred_count > 0;
                const statusBg = isActive ? "#dcfce7" : isSubmitted ? "#fef9c3" : "#fee2e2";
                const statusColor = isActive ? "#15803d" : isSubmitted ? "#92400e" : "#dc2626";
                const statusLabel = isActive ? "✅ Active" : isSubmitted ? "📋 Onboarding Submitted" : "🕐 Onboarding Started";
                return (
                  <div key={u.id||i} style={{ background:"#fff", border:`1px solid ${isTemp?"#fecaca":isSubmitted?"#fde68a":"#bbf7d0"}`, borderRadius:12, padding:"14px 18px", boxShadow:"0 1px 4px rgba(0,0,0,.05)" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:8, marginBottom:10 }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:15, color:"#1e293b" }}>{u.name}</div>
                        <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>
                          <span style={{ fontFamily:"monospace", background:"#f1f5f9", padding:"1px 6px", borderRadius:4, marginRight:6 }}>{u.emp_id}</span>
                          {u.email} · {u.role === "intern" ? "👨‍🎓 Intern" : "👤 Employee"}
                          {u.department && ` · ${u.department}`}
                        </div>
                        <div style={{ fontSize:11, color:"#94a3b8", marginTop:2 }}>
                          Created: {u.created_at ? new Date(u.created_at).toLocaleString("en-IN", {day:"2-digit",month:"short",year:"numeric"}) : "—"}
                        </div>
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                        <span style={{ background:statusBg, color:statusColor, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700 }}>{statusLabel}</span>
                        <div style={{ display:"flex", gap:6 }}>
                          <span style={{ background:hasAssets?"#dcfce7":"#fef2f2", color:hasAssets?"#15803d":"#dc2626", padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:600 }}>
                            💻 {hasAssets ? `${u.asset_count} asset${u.asset_count>1?"s":""}` : "No assets"}
                          </span>
                          <span style={{ background:hasCreds?"#dbeafe":"#fef2f2", color:hasCreds?"#1d4ed8":"#dc2626", padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:600 }}>
                            🔑 {hasCreds ? "Credentials set" : "No credentials"}
                          </span>
                        </div>
                      </div>
                    </div>
                    {(!hasAssets || !hasCreds) && (
                      <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:12, color:"#92400e", display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:16 }}>⚠️</span>
                        <span>
                          {!hasAssets && !hasCreds ? "No assets assigned and no IT credentials created yet." :
                           !hasAssets ? "No assets assigned yet." :
                           "IT credentials not created yet."}
                          {isActive ? " Employee is active — please set up IT access now." : " Will need setup once onboarding is approved."}
                        </span>
                      </div>
                    )}
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      <button
                        onClick={() => { setActiveTab("assets"); setTimeout(() => document.querySelector(".itaassetspg-inline-3")?.click(), 100); }}
                        style={{ padding:"6px 14px", background:"#4f46e5", color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                        💻 Add Asset
                      </button>
                      <button
                        onClick={() => { setActiveTab("credentials"); }}
                        style={{ padding:"6px 14px", background:"#0284c7", color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                        🔑 Set Credentials
                      </button>
                      {isActive && !hasAssets && (
                        <span style={{ fontSize:11, color:"#ef4444", fontWeight:600, alignSelf:"center" }}>
                          🔴 Urgent — employee is active without assets!
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab==="assets"&&(<>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:16 }}>
          {FILTERS.map(f=><StatBadge key={f} label={f==="all"?"All":f} count={counts[f==="all"?"all":f]??0} color={STATUS_COLORS[f]} active={filter===f} onClick={()=>setFilter(f)} />)}
        </div>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:14, alignItems:"center" }}>
          <span style={{ fontSize:12, color:"#64748b", fontWeight:600 }}>Month:</span>
          <select value={monthFilter} onChange={e=>setMonthFilter(e.target.value)} style={{ padding:"7px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, color:"#374151", background:"#fff", cursor:"pointer" }}>
            <option value="all">All Months</option>
            {availMonths.filter(m=>m!=="all").map(m=><option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
          {(filter!=="all"||monthFilter!=="all")&&<><button onClick={()=>{setFilter("all");setMonthFilter("all");}} style={{ padding:"6px 12px", border:"1px solid #fecaca", borderRadius:8, background:"#fef2f2", color:"#ef4444", fontSize:11, fontWeight:600, cursor:"pointer" }}>✕ Reset Filters</button><span style={{ fontSize:12, color:"#64748b" }}>Showing <strong style={{color:"#1e293b"}}>{filtered.length}</strong> of {aList.length} assets</span></>}
        </div>
        {monthFilter!=="all"&&(
          <div style={{ background:"linear-gradient(135deg,#1e1b4b,#4f46e5)", borderRadius:10, padding:"12px 18px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center", color:"#fff" }}>
            <div><div style={{ fontWeight:700, fontSize:14 }}>📅 {monthLabel(monthFilter)}</div><div style={{ fontSize:11, opacity:.8, marginTop:2 }}>{filtered.length} assets in this month</div></div>
            <div style={{ display:"flex", gap:14 }}>{["Assigned","Available","Returned","Damaged","Lost"].map(s=>{ const mc=filtered.filter(a=>(s==="Lost"?(a.status==="Lost"||a.condition==="Lost"):a.status===s)).length; return mc>0?<div key={s} style={{ textAlign:"center" }}><div style={{ fontSize:18, fontWeight:800 }}>{mc}</div><div style={{ fontSize:9, opacity:.75 }}>{s}</div></div>:null; })}</div>
            <button onClick={()=>exportCSV(filtered,`assets_${monthFilter}.csv`)} style={{ padding:"7px 14px", background:"rgba(255,255,255,.2)", border:"1px solid rgba(255,255,255,.3)", borderRadius:8, color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>⬇ Download Month CSV</button>
          </div>
        )}
        <div className="card animate-in">
          <div className="card-header">
            <div className="card-title">{filter==="all"?"All Assets":`${filter} Assets`}{monthFilter!=="all"&&<span style={{ fontSize:12, color:"#64748b", fontWeight:400, marginLeft:8 }}>· {monthLabel(monthFilter)}</span>}</div>
            <div className="card-subtitle">{filtered.length} items</div>
          </div>
          <div className="card-body itaassetspg-inline-6">
            {loading?<div className="itaassetspg-inline-7">Loading assets...</div>
            :filtered.length===0?<div className="itaassetspg-inline-8">No assets found.</div>:(
              <div style={{ overflowX:"auto" }}>
                <table className="data-table">
                  <thead><tr>
                    <th>Asset ID</th><th>Tag</th><th>Type</th><th>Brand</th><th>Serial</th>
                    <th>Value (₹)</th><th>Assigned To</th>
                    <th style={{color:"#7c3aed"}}>Issued Date</th>
                    <th style={{color:"#7c3aed"}}>Issued By</th>
                    <th>Purchased</th><th>Condition</th><th>Status</th><th>Warranty</th>
                    <th>Last Maint.</th><th>Next Maint.</th>
                    <th style={{color:"#2563eb",minWidth:160}}>Last Assigned</th>
                    <th>Actions</th>
                  </tr></thead>
                  <tbody>{filtered.map(a=>{
                    const cc=conditionColor(a.condition); const sc=statusColor(a.status);
                    const maintDue=a.next_maintenance_date&&new Date(a.next_maintenance_date)<=new Date();
                    return (
                      <tr key={a.aid}>
                        <td style={{ fontWeight:600, color:"#4f46e5" }}>{a.aid}</td>
                        <td style={{ color:"#64748b" }}>{a.asset_tag||"—"}</td>
                        <td>{a.assetType}</td>
                        <td style={{ fontWeight:500 }}>{a.brand}</td>
                        <td style={{ fontFamily:"monospace", fontSize:11 }}>{a.serial}</td>
                        <td style={{ fontWeight:600, color:"#059669" }}>₹{parseFloat(a.asset_value||0).toLocaleString("en-IN")}</td>
                        <td>{a.empName||<span style={{color:"#94a3b8"}}>—</span>}</td>
                        <td style={{ fontSize:12, color:a.issued?"#374151":"#94a3b8" }}>{fmtDate(a.issued)||"—"}</td>
                        <td style={{ fontSize:12 }}>{a.issued_by?<span style={{ background:"#ede9fe", color:"#7c3aed", padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:600 }}>{a.issued_by}</span>:<span style={{color:"#94a3b8"}}>—</span>}</td>
                        <td style={{ fontSize:12 }}>{fmtDate(a.purchased)||"—"}</td>
                        <td><span style={{ background:cc.bg, color:cc.color, padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:600 }}>{a.condition}</span></td>
                        <td><span style={{ background:sc.bg, color:sc.color, padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:600 }}>{a.status}</span></td>
                        <td style={{ fontSize:11, color:a.warrantyDate?"#d97706":"#64748b" }}>{fmtDate(a.warrantyDate)||a.warranty||"—"}</td>
                        <td style={{ fontSize:11 }}>{fmtDate(a.last_maintenance_date)||"—"}</td>
                        <td style={{ fontSize:11, color:maintDue?"#dc2626":"#64748b", fontWeight:maintDue?700:400 }}>{a.next_maintenance_date?<span>{fmtDate(a.next_maintenance_date)}{maintDue?" ⚠️":""}</span>:"—"}</td>
                        <td><LastAssignedBadge asset={a} /></td>
                        <td>
                          <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                            <button onClick={()=>openEdit(a)} className="itaassetspg-inline-16">✏️ Edit</button>
                            <button onClick={()=>setShowMaintenance(a)} style={{ padding:"3px 7px", fontSize:11, background:"#eef2ff", color:"#4f46e5", border:"1px solid #c7d2fe", borderRadius:4, cursor:"pointer" }}>🔧</button>
                            <button onClick={()=>deleteAsset(a)} className="itaassetspg-inline-17">🗑️</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </>)}

      {/* ADD MODAL */}
      <ITAModal open={showAdd} onClose={()=>setShowAdd(false)} title="Add New Asset">
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <div className="form-group"><label className="form-label">Asset Type *</label><select className="form-input" value={form.assetType} onChange={e=>setForm({...form,assetType:e.target.value})}><option>Laptop</option><option>Desktop</option><option>Monitor</option><option>Mobile</option><option>Tablet</option><option>Accessory</option><option>Other</option></select></div>
          <div className="form-group"><label className="form-label">Asset Tag</label><input className="form-input" maxLength={20} placeholder="e.g. LPT-001" value={form.assetTag} onChange={e=>setForm({...form,assetTag:e.target.value})} /></div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <div className="form-group"><label className="form-label">Brand / Model *</label><input className="form-input" value={form.brand} onChange={e=>setForm({...form,brand:e.target.value})} placeholder="e.g. Dell Latitude 5420" /></div>
          <div className="form-group"><label className="form-label">Serial Number *</label><input className="form-input" value={form.serial} onChange={e=>setForm({...form,serial:e.target.value})} placeholder="e.g. C02Z12AB" /></div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <div className="form-group"><label className="form-label">Asset Value (₹)</label><input type="number" className="form-input" min="0" step="0.01" placeholder="0.00" value={form.assetValue} onChange={e=>setForm({...form,assetValue:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Condition</label><select className="form-input" value={form.condition} onChange={e=>setForm({...form,condition:e.target.value})}><option>New</option><option>Good</option><option>Fair</option><option>Poor</option></select></div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <div className="form-group"><label className="form-label">Purchased Date</label><input type="date" className="form-input" value={form.purchasedDate} onChange={e=>setForm({...form,purchasedDate:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Warranty Expiry Date</label><input type="date" className="form-input" value={form.warrantyDate} onChange={e=>setForm({...form,warrantyDate:e.target.value})} /></div>
        </div>
        {/* Issue Details */}
        {/* <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:8, padding:"10px 12px", marginBottom:4 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#7c3aed", marginBottom:8 }}>🏷️ Issue Details <span style={{ fontSize:10, background:"#ede9fe", color:"#7c3aed", padding:"1px 6px", borderRadius:10 }}>NEW</span></div> */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Issued Date</label>
              <input type="date" className="form-input" value={form.issuedDate} onChange={e=>setForm({...form,issuedDate:e.target.value})} />
              <div style={{ fontSize:10, color:"#94a3b8", marginTop:3 }}>Leave blank → auto-set on assignment</div>
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Issued By (IT Admin)</label>
              <select className="form-input" value={form.issuedBy} onChange={e=>setForm({...form,issuedBy:e.target.value})}>
                <option value="">— Select IT Admin —</option>
                {itAdmins.map(a=><option key={a.id} value={a.name}>{a.name}</option>)}
              </select>
            </div>
          </div>
        {/* </div> */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <div className="form-group"><label className="form-label">Last Maintenance Date</label><input type="date" className="form-input" value={form.lastMaintenanceDate} onChange={e=>setForm({...form,lastMaintenanceDate:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Next Maintenance Date</label><input type="date" className="form-input" value={form.nextMaintenanceDate} onChange={e=>setForm({...form,nextMaintenanceDate:e.target.value})} /></div>
        </div>
        <div className="form-group"><label className="form-label">Assign to Employee (optional)</label>
          <select className="form-input" value={form.empId} onChange={e=>setForm({...form,empId:e.target.value})}>
            <option value="">— Unassigned (Inventory) —</option>
            {employees.filter(p=>p.status!=="Exiting").map(p=><option key={p.id} value={p.id}>{p.name} ({p.id})</option>)}
          </select>
        </div>
        <div className="form-actions" style={{ justifyContent:"flex-end", gap:10, marginTop:4 }}>
          <button className="btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button>
          <button className="btn-submit" onClick={addAsset}>Add Asset</button>
        </div>
      </ITAModal>

      {/* EDIT MODAL */}
      <ITAModal open={!!showEdit} onClose={()=>setShowEdit(null)} title={`Edit Asset — ${showEdit?.aid||""}`}>
        {showEdit&&(
          <>
            <div style={{ background:"#f8fafc", borderRadius:8, padding:"8px 12px", marginBottom:12 }}>
              <div style={{ fontWeight:600, color:"#1e293b" }}>{showEdit.brand}</div>
              <div style={{ fontSize:12, color:"#64748b" }}>{showEdit.serial} · {showEdit.assetType}</div>
            </div>
            {showEdit.last_assigned_emp&&(
              <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"10px 12px", marginBottom:12 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#1d4ed8", marginBottom:6 }}>📋 Assignment History</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                  <div><div style={{ fontSize:10, color:"#64748b" }}>Last Assigned To</div><div style={{ fontSize:13, fontWeight:600, color:"#1e293b" }}>{showEdit.last_assigned_emp}</div>{showEdit.last_assigned_emp_id&&<div style={{ fontSize:10, color:"#64748b" }}>ID: {showEdit.last_assigned_emp_id}</div>}</div>
                  <div><div style={{ fontSize:10, color:"#64748b" }}>Reassigned On</div><div style={{ fontSize:13, fontWeight:600, color:"#1e293b" }}>{fmtDate(showEdit.last_assignment_date)}</div></div>
                  <div><div style={{ fontSize:10, color:"#64748b" }}>Held For</div><div style={{ fontSize:13, fontWeight:700, color:"#15803d" }}>{calcDuration(showEdit.prev_assignment_start,showEdit.prev_assignment_end)||"—"}</div>{showEdit.prev_assignment_start&&<div style={{ fontSize:10, color:"#64748b" }}>{fmtDate(showEdit.prev_assignment_start)} → {fmtDate(showEdit.prev_assignment_end)}</div>}</div>
                </div>
              </div>
            )}
            <div className="form-group"><label className="form-label">Condition</label>
              <select className="form-input" value={editForm.condition} onChange={e=>setEditForm({...editForm,condition:e.target.value,status:e.target.value==="Lost"?"Lost":editForm.status})}>
                <option>New</option><option>Good</option><option>Fair</option><option>Poor</option><option>Damaged</option><option>Lost</option>
              </select>
            </div>
            {editForm.condition!=="Lost"&&(
              <>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  <div className="form-group"><label className="form-label">Status</label><select className="form-input" value={editForm.status} onChange={e=>setEditForm({...editForm,status:e.target.value})}><option>Assigned</option><option>Available</option><option>Returned</option><option>Damaged</option></select></div>
                  <div className="form-group"><label className="form-label">Asset Tag</label><input className="form-input" maxLength={20} value={editForm.assetTag} onChange={e=>setEditForm({...editForm,assetTag:e.target.value})} /></div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  <div className="form-group"><label className="form-label">Asset Value (₹)</label><input type="number" className="form-input" min="0" step="0.01" value={editForm.assetValue} onChange={e=>setEditForm({...editForm,assetValue:e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">Warranty Date</label><input type="date" className="form-input" value={editForm.warrantyDate} onChange={e=>setEditForm({...editForm,warrantyDate:e.target.value})} /></div>
                </div>
                <div className="form-group"><label className="form-label">Assigned Employee</label>
                  <select className="form-input" value={editForm.empId} onChange={e=>setEditForm({...editForm,empId:e.target.value})}>
                    <option value="">— Unassigned —</option>
                    {employees.map(p=><option key={p.id} value={p.id}>{p.name} ({p.id})</option>)}
                  </select>
                  {editForm.empId&&showEdit.empId&&editForm.empId!==showEdit.empId&&<div style={{ marginTop:6, padding:"6px 10px", background:"#fef3c7", borderRadius:6, fontSize:11, color:"#92400e" }}>⚠️ Reassigning — current employee will be saved as "Last Assigned"</div>}
                </div>
              </>
            )}
            {editForm.condition==="Lost"&&(
              <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"14px", marginBottom:10 }}>
                <div style={{ fontWeight:700, color:"#dc2626", fontSize:13, marginBottom:12 }}>⚠️ Lost Asset — Auto Report will be Generated</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                  <ReadField label="Asset Value" val={`₹${parseFloat(showEdit.asset_value||0).toLocaleString("en-IN")}`} />
                  <ReadField label="Warranty Date" val={fmtDate(showEdit.warrantyDate||showEdit.warranty_date)} />
                  <ReadField label="Issued Date" val={fmtDate(showEdit.issued)} />
                </div>
                <div style={{ marginTop:10, fontSize:12, color:"#64748b", lineHeight:1.6 }}>📋 Clicking <strong>"Generate Report"</strong> will open a form with employee + PM details.</div>
              </div>
            )}
            <div className="form-actions" style={{ justifyContent:"flex-end", gap:10, marginTop:8 }}>
              <button className="btn-outline" onClick={()=>setShowEdit(null)}>Cancel</button>
              <button className="btn-submit" style={editForm.condition==="Lost"?{background:"#dc2626"}:{}} onClick={saveEdit}>{editForm.condition==="Lost"?"📋 Generate Report →":"Save Changes"}</button>
            </div>
          </>
        )}
      </ITAModal>

      {showMaintenance&&<MaintenanceModal asset={showMaintenance} onClose={()=>setShowMaintenance(null)} onSave={async()=>{ showSuccess("Maintenance updated!"); await fetchAssets(); }} />}
      {showLostForm&&<LostAssetFormModal asset={showLostForm} onClose={()=>setShowLostForm(null)} onSave={async()=>{ await apiCall("/itadmin/assets",{method:"PUT",body:JSON.stringify({assetId:showLostForm.aid,assetCondition:"Lost",status:"Lost",empId:showLostForm.empId})}).catch(()=>{}); showSuccess("Lost asset report sent to PM!"); await fetchAssets(); }} />}
    </div>
  );
}

function ReadField({ label, val }) {
  return (
    <div style={{ background:"#fff", borderRadius:8, padding:"8px 10px" }}>
      <div style={{ fontSize:10, color:"#94a3b8", textTransform:"uppercase", marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:13, fontWeight:700, color:"#1e293b" }}>{val || "—"}</div>
    </div>
  );
}

export default ITAAssetsPg;
